import { TranslationKey } from './../../../../types/translation/TranslationKey';
import React, { useContext, useEffect, useState } from 'react';
import { translate } from '../../../../types/translation/Translator';
import { Route, Routes, useLocation, useNavigate, useParams } from 'react-router-dom';
import ProductMasterDataInfoPane from './Panes/ProductMasterDataInfoPane';
import { ProductMasterData } from '../../../../types/product_master_data/productMasterData';
import { ProductMasterDataContext } from '../../../../context/ProductMasterDataContext';
import {
  ProductMasterDataMutations,
  UpdateProductMasterDataResponse,
  UpdateProductMasterDataVariables,
} from '../../../../graphql/productMasterData.graphql';
import ProductMasterDataAttributePane from './Panes/ProductMasterDataAttributePane';
import ProductMasterDataCommentPane from './Panes/ProductMasterDataCommentPane';
import ProductMasterDataReorderRulesPane from './Panes/ProductMasterDataReorderRulesPane';
import NotFound from '../../../../assets/html/notFound';
import { CompanyContext } from '../../../../context/CompanyContext';
import FilesForEntityPane from '../../../../VentoryUI/components/common/File/FilesForEntityPane';
import { FileEntityType } from '../../../../types/file';
import { Routing } from '../../../../util/routes.util';
import { EntityTagMultiSelect } from '../../../components/Dropdown';
import { EntityType } from '../../../../types/comment';
import HandlingUnitsOverviewPane from '../Settings/Panes/HandlingUnits/HandlingUnitsOverviewPane';
import ProductMasterDataLabelsPane from './Panes/ProductMasterDataLabelsPane';
import ProductMasterDataKitPane from './Panes/ProductMasterDataKitPane';
import { CompanyRoleAssignmentContext } from '../../../../context/CompanyRoleAssignmentContext';
import { ProductMasterDataInput } from '../../../../types/product_master_data';
import { Tab, Tabs } from '../../../components/Tabs';
import useMutation from '../../../../hooks/useMutation';
import { BackButton, SaveButton } from '../../../components/Button';
import { Breadcrumbs, Crumb } from '../../../components/Breadcrumbs';
import ProductMasterDataOverviewCrumb from '../../../components/Breadcrumbs/crumbs/ProductMasterDataOverviewCrumb';

const tabs = (productMasterData: ProductMasterData, isAdmin: boolean) =>
  [
    {
      text: translate({ key: TranslationKey.info }),
      path: 'info',
      key: 'info',
    },
    {
      text: translate({ key: TranslationKey.attributes }),
      path: 'attributes',
      key: 'attributes',
    },
    {
      text: translate({ key: TranslationKey.label }),
      path: 'labels',
      key: 'labels',
    },
    {
      text: translate({ key: TranslationKey.comment }),
      path: 'comments',
      key: 'comments',
    },
    {
      text: translate({ key: TranslationKey.stockTriggers }),
      path: 'stock_triggers',
      key: 'stock_triggers',
    },
    {
      text: translate({ key: TranslationKey.filesAndAssets }),
      path: 'files',
      key: 'files',
    },
    {
      text: translate({ key: TranslationKey.handlingUnit }),
      path: 'handling_units',
      key: 'handling_units',
    },
    {
      text: translate({ key: TranslationKey.kit }),
      path: 'kits',
      key: 'kits',
    },
  ].filter(tab => {
    if (tab.key === 'kits' && !productMasterData.kitManaged) return false;
    if (tab.key === 'handling_units' && productMasterData.handlingUnit) return false;

    if (!isAdmin && tab.key !== 'info' && tab.key !== 'attributes' && tab.key !== 'files') return false;

    return true;
  });

export default function UpdateProductMasterDataPane({}) {
  const navigate = useNavigate();
  const location = useLocation();

  const id = useParams()['id'] || '';

  const { productMasterData, setProductMasterData, productMasterDataLoading } = useContext(ProductMasterDataContext);
  const { currentCompany } = useContext(CompanyContext);
  const { isAdmin } = useContext(CompanyRoleAssignmentContext);

  const existing = productMasterData.get(id);

  const [input, setInput] = useState<ProductMasterDataInput>(
    new ProductMasterDataInput(existing || { companyId: currentCompany.id }),
  );

  const [update, { loading }] = useMutation<UpdateProductMasterDataResponse, UpdateProductMasterDataVariables>(
    ProductMasterDataMutations.update,
    {
      onCompleted: res => {
        const pmd = res.updateProductMasterData[0];
        setProductMasterData(new Map(productMasterData).set(pmd.id, new ProductMasterData(pmd)));
        navigate(Routing.productMasterData.overview());
      },
    },
  );

  useEffect(() => {
    if (!input.id) {
      setInput(new ProductMasterDataInput(productMasterData.get(id) || { companyId: currentCompany.id }));
    }
  }, [productMasterData]);

  if (!existing) return <NotFound />;

  const handleUpdate = async () => {
    await update({
      variables: {
        productMasterData: [input.forUpdate()],
      },
    });
  };

  const isInfo = location.pathname.endsWith('info');
  const isAttributes = location.pathname.endsWith('attributes');

  const showFooter = isInfo || isAttributes;

  return (
    <div className='flex h-full flex-col'>
      <Breadcrumbs>
        <ProductMasterDataOverviewCrumb />
        <Crumb text={existing.productNumber} path={Routing.productMasterData.update(existing.id).info()} current />
      </Breadcrumbs>

      {/* This should be re-evaluated, probably the tabs should handle their padding (info pane wants to handle its own padding so the scrollbar is on the side of the screen rather than being pushed in by the padding here) */}
      <div className={`${isInfo ? '' : 'content-x'} content-y-breadcrumbs flex flex-grow flex-col overflow-hidden`}>
        <Tabs padding={isInfo}>
          {tabs(existing, !!isAdmin).map(tab => (
            <Tab key={tab.key} path={tab.path} text={tab.text} />
          ))}
        </Tabs>

        <div className='flex h-full flex-col gap-16 overflow-hidden'>
          <Routes>
            <Route
              path='info'
              element={
                <ProductMasterDataInfoPane
                  input={input}
                  setInput={setInput}
                  tagInput={<EntityTagMultiSelect entityId={input.id} entityType={EntityType.pmd} />}
                />
              }
            />
            <Route path='attributes' element={<ProductMasterDataAttributePane input={input} setInput={setInput} />} />
            <Route
              path='comments'
              element={
                <ProductMasterDataCommentPane productMasterData={productMasterData.get(id)} footer={() => <></>} />
              }
            />
            <Route path='kits' element={<ProductMasterDataKitPane pmdId={id} footer={() => <></>} />} />
            <Route path='labels' element={<ProductMasterDataLabelsPane pmdId={id} footer={() => <></>} />} />
            <Route
              path='stock_triggers/*'
              element={<ProductMasterDataReorderRulesPane productMasterData={productMasterData.get(id)} />}
            />
            <Route
              path='files'
              element={<FilesForEntityPane entityId={id} entityType={FileEntityType.pmd} setError={() => {}} />}
            />
            <Route path='handling_units' element={<HandlingUnitsOverviewPane pmdId={id} />} />
          </Routes>

          {showFooter ? (
            <div className={`${isInfo ? 'content-x' : ''} flex gap-2`}>
              <BackButton disabled={loading} onClick={() => navigate(Routing.productMasterData.overview())} />
              {/* TODO: Should this be admin only? Backend restricts to employee */}
              {isAdmin ? (
                <SaveButton
                  loading={loading}
                  onClick={() => handleUpdate()}
                  disabled={!input.productName || !input.productNumber}
                />
              ) : null}
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
}
